import { useState, useEffect, useRef } from "react";

const WA_NUMBER = "85253482337";

const quickMessages = [
  {
    id: "price",
    icon: "ri-price-tag-3-line",
    label: "查詢行程價格",
    sub: "我想了解各路線的報價",
    text: "你好，我想查詢行程價格，請問可以提供報價嗎？",
    color: "bg-orange-50 text-orange-600 hover:bg-orange-100",
    iconColor: "text-orange-500",
  },
  {
    id: "itinerary",
    icon: "ri-map-2-line",
    label: "查詢指定路線",
    sub: "了解特定目的地詳情",
    text: "你好，我想查詢自駕旅遊路線的詳細資訊，請問有什麼可以推薦？",
    color: "bg-amber-50 text-amber-700 hover:bg-amber-100",
    iconColor: "text-amber-500",
  },
  {
    id: "custom",
    icon: "ri-edit-line",
    label: "自訂行程查詢",
    sub: "完全按我的需求設計",
    text: "你好，我想查詢自訂行程服務，希望可以度身訂造屬於自己的路線。",
    color: "bg-green-50 text-green-700 hover:bg-green-100",
    iconColor: "text-green-600",
  },
  {
    id: "general",
    icon: "ri-customer-service-2-line",
    label: "一般查詢",
    sub: "其他問題都可以問",
    text: "你好，我有一些關於自駕旅遊的問題想查詢。",
    color: "bg-stone-50 text-stone-700 hover:bg-stone-100",
    iconColor: "text-stone-500",
  },
];

const WhatsAppButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        btnRef.current &&
        !btnRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleQuickMsg = (text: string) => {
    const encoded = encodeURIComponent(text);
    window.open(`https://wa.me/${WA_NUMBER}?text=${encoded}`, "_blank", "noopener,noreferrer");
    setIsOpen(false);
  };

  return (
    <>
      {/* WhatsApp Panel */}
      <div
        ref={panelRef}
        className={`fixed right-8 z-40 transition-all duration-300 ease-out ${
          showScrollTop ? "bottom-28" : "bottom-8"
        } ${
          isOpen
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-3 pointer-events-none"
        }`}
        style={{ width: "280px" }}
      >
        <div className="bg-white rounded-2xl overflow-hidden border border-stone-100" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
          {/* Panel Header */}
          <div className="bg-green-500 px-4 py-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-whatsapp-line text-white text-xl"></i>
            </div>
            <div>
              <p className="text-white font-semibold text-sm">Chill Go 客服</p>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-green-200 rounded-full inline-block animate-pulse"></span>
                <span className="text-green-100 text-xs">通常在數分鐘內回覆</span>
              </div>
            </div>
          </div>

          {/* Quick Options */}
          <div className="p-3 space-y-2">
            <p className="text-xs text-gray-400 font-medium px-1 pt-1 pb-0.5">快速查詢選項</p>
            {quickMessages.map((msg) => (
              <button
                key={msg.id}
                onClick={() => handleQuickMsg(msg.text)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer text-left ${msg.color}`}
              >
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-white flex-shrink-0`}>
                  <i className={`${msg.icon} text-base ${msg.iconColor}`}></i>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-tight">{msg.label}</p>
                  <p className="text-xs opacity-70 mt-0.5 leading-tight">{msg.sub}</p>
                </div>
                <i className="ri-arrow-right-s-line text-gray-400 flex-shrink-0 ml-auto"></i>
              </button>
            ))}
          </div>

          {/* Footer note */}
          <div className="px-4 pb-3 pt-1">
            <p className="text-center text-xs text-gray-400">
              點擊後將開啟 WhatsApp 對話
            </p>
          </div>
        </div>

        {/* Arrow pointing to button */}
        <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-stone-100 rotate-45 transform"></div>
      </div>

      {/* WhatsApp Floating Button */}
      <button
        ref={btnRef}
        onClick={() => setIsOpen((v) => !v)}
        aria-label="WhatsApp 快速查詢"
        className={`fixed right-8 z-40 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
          showScrollTop ? "bottom-28" : "bottom-8"
        } ${
          isOpen
            ? "bg-green-600 rotate-[360deg] scale-110"
            : "bg-green-500 hover:bg-green-600 hover:scale-110"
        }`}
        style={{ boxShadow: "0 4px 20px rgba(34,197,94,0.45)" }}
      >
        <i className={`text-white text-2xl transition-all duration-200 ${isOpen ? "ri-close-line" : "ri-whatsapp-line"}`}></i>
      </button>

      {/* Pulse ring animation */}
      {!isOpen && (
        <span
          className={`fixed right-8 z-39 w-14 h-14 rounded-full bg-green-400 opacity-0 animate-ping pointer-events-none ${
            showScrollTop ? "bottom-28" : "bottom-8"
          }`}
        ></span>
      )}

      {/* Scroll To Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="回到頂部"
        className={`fixed bottom-8 right-8 z-40 w-12 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
          showScrollTop
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-4 pointer-events-none"
        }`}
      >
        <i className="ri-arrow-up-line text-lg"></i>
      </button>
    </>
  );
};

export default WhatsAppButton;
